import { RecurrenceInputBlock } from '@coast/schemas';
import { useCallback, useMemo, useState } from 'react';
import { ActionSheet } from '@shared/components/ActionSheet';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { getTextPropsFromTextStyle } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { getAnchorName } from '@shared/styles';
import { renderClearButton } from '../input';
import { MBCardInputBlockBase } from '../input/base';
import {
  useIsRecurrenceFieldAvailable,
  usePresubmitRecurrenceUpdateScopePrompt,
  useRequestRecurrenceScope,
  useResetRecurrenceIfNoDateValue,
  useResetRecurrenceIfNotAvailable,
} from './hooks';
import { MBRecurrencePicker } from './picker';
import { toString } from './utils';

interface Props {
  block: RecurrenceInputBlock;
}

export function MBCardInputBlockRecurrence({ block }: Props) {
  const { values } = useCardFormContext();
  const value = values[block.name] as string;
  const string = useMemo(() => {
    if (!value) {
      return null;
    }
    return toString(value);
  }, [value]);

  const textProps = getTextPropsFromTextStyle(block.textStyle);

  const anchorName = getAnchorName('recurrence-input-block', block.name);

  const [pickerVisible, setPickerVisible] = useState(false);
  const showPicker = useCallback(() => setPickerVisible(true), []);
  const hidePicker = useCallback(() => setPickerVisible(false), []);

  const scopeMenu = useRequestRecurrenceScope();
  usePresubmitRecurrenceUpdateScopePrompt(block, scopeMenu.requestScope);
  useResetRecurrenceIfNoDateValue(block);
  useResetRecurrenceIfNotAvailable(block);
  const available = useIsRecurrenceFieldAvailable(block);

  if (!available) {
    return null;
  }
  if (!block.dateFieldName) {
    return (
      <Text {...textProps} danger>
        Warning: cannot render recurrence input without a referenced date. Did you forget to specify
        block.dateFieldName?
      </Text>
    );
  }

  return (
    <>
      <MBTouchableOpacity onPress={showPicker}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderRight={renderClearButton}>
            <Text {...textProps} inactive={!value}>
              {value ? string : block.placeholder}
            </Text>
          </MBCardInputBlockBase>
        </View>
      </MBTouchableOpacity>
      {pickerVisible && (
        <MBRecurrencePicker block={block} onClose={hidePicker} anchorName={anchorName} />
      )}
      {scopeMenu.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          title={scopeMenu.title}
          options={scopeMenu.options}
          onClose={scopeMenu.onClose}
        />
      )}
    </>
  );
}
